// 무빈소 신규 리드 퍼널(디자인: design_handoff_mubinso_funnels) 관련 상수 모음.
// /match/request 진입에서 광고 무빈소 키워드 OR ?template=mubinso 쿼리일 때 노출.
// 질문 순서: 1) 관심 주제 → 2) 지금 상황. (실험 종료 후 순서 스왑)
// 개편 실험 330 종료: A 승. '절차'·'세가지 다' 진입만 B(절차 개편) 채택.

import { LeadUrgencyStatus } from "@/apis/postLeadV2";

/** 무빈소 신규 퍼널 A/B Hackle 실험 번호. */
export const MUBINSO_FUNNEL_EXPERIMENT_KEY = 314;

/** 어떤 퍼널로 진행할지. 메인/절차/비용/찾기. */
export type MubinsoFunnelType = "main" | "procedure" | "cost" | "parlorFind";

/**
 * 실험 모수의 진입 구분. Q1(니즈)과 Q2(상황)의 조합이다.
 *
 * 렌더된 퍼널이 아니라 **진입** 기준이어야 개편 전후 지표를 같은 축에서 비교할 수 있다.
 * "세가지 다" 는 절차 퍼널을 보지만 topic_all 로 태깅한다(절차 진입과 섞이면 안 된다).
 */
export type MubinsoEntry =
  | "urgent"
  | "topic_procedure"
  | "topic_all"
  | "topic_cost"
  | "topic_parlor"
  // M5 가족장 퍼널(isFamilyFunnel). Q1·Q2 와 상관없이 가족장 진입은 하나로 묶는다(A 지표와 섞지 않는다)
  | "family";

/**
 * 진입별 이벤트 prefix. GA4 는 속성보다 이벤트명으로 보는 게 쉬워서,
 * 퍼널별 지표는 속성 필터가 아니라 **개별 이벤트명**으로 만든다.
 *
 * 행동마다 공통 이벤트 1개 + 개별 이벤트 1개를 발화한다.
 *   전환 화면 도달 : mubinso_request_complete(action) + {p}-conv_view
 *   주 CTA 클릭    : conv-info_cta               + {p}-cta_main
 *   리드 제출 시도 : lead_mdl-submit             + {p}-lead_try
 *   리드 제출 성공 : lead_mdl-success            + {p}-lead_ok
 *
 * 공통 이벤트는 개편 전 baseline 과 이어지고, 개별 이벤트로 퍼널별 A/B 를 쪼갠다.
 * ⚠️ 합산 지표에는 공통 이벤트만 쓸 것. 둘을 더하면 같은 행동이 두 번 세어진다.
 *
 * A(대조군)에서도 같은 개별 이벤트를 발화한다. 안 그러면 A 의 퍼널별 수치가 없어
 * 비교 자체가 성립하지 않는다. 화면은 건드리지 않고 계측만 붙는다.
 */
export const MUBINSO_ENTRY_PREFIX = {
  urgent: "urg",
  topic_procedure: "prc",
  topic_all: "all",
  topic_cost: "cst",
  // 장례식장 찾기는 330 개편 밖이라 빠져 있었다. E4 에서 A·B 를 퍼널별로 비교하려고 더한다
  topic_parlor: "fnd",
  family: "fam",
} as const;

export type MubinsoEntryPrefix = typeof MUBINSO_ENTRY_PREFIX[MubinsoEntry];

/** 진입별 개별 이벤트 트리거. path 가 14자라 예산 19자, 전부 11~13자로 잘리지 않는다. */
export const mubinsoEntryTrigger = <S extends "conv_view" | "cta_main" | "lead_try" | "lead_ok">(
  entry: MubinsoEntry,
  suffix: S,
): `${MubinsoEntryPrefix}-${S}` => `${MUBINSO_ENTRY_PREFIX[entry]}-${suffix}`;

/**
 * 리드 제출 성공 때 고른 상황(임박도)별 개별 이벤트. {p}-sit_{now|week|month|notyet}
 *
 * A 는 Q2 답, E4 는 리드 폼 상황 선택이라 묻는 자리가 달라 DB status 로만 남았다.
 * 접미사는 회사 공통 임박도 키(LEAD_URGENCY_EVENT_KEY, goi_lead_*)와 같다.
 * A · B at-needs 합계는 goi_lead_now · week 로 본다. A 는 임종 · 임박이 메인 퍼널(urg)로 가서
 * 퍼널별로는 A · B 비교가 안 되고, 이 이벤트는 B 안에서 퍼널별로 나눠 볼 때 쓴다.
 * 급한 고객(urg)은 상황이 정해져 있어, 가족장(fam)은 E4 비교 밖이라 쏘지 않는다. 전부 14자 이하라 잘리지 않는다.
 */
const MUBINSO_SITUATION_SUFFIX = {
  임종상태: "sit_now",
  임박: "sit_week",
  "1달": "sit_month",
  여유: "sit_notyet",
} as const;

export const mubinsoSituationTrigger = (
  entry: Exclude<MubinsoEntry, "urgent" | "family">,
  status: LeadUrgencyStatus,
) => `${MUBINSO_ENTRY_PREFIX[entry]}-${MUBINSO_SITUATION_SUFFIX[status]}` as const;

/** 컨테이너 내부 스크린 상태(라우트 없이 상태머신으로만 전환). */
export type MubinsoScreen =
  | "landing"
  | "topic"
  | "situation"
  | "funnel"
  | "guide"
  // 가족장 퍼널 전용. 랜딩 대신 소개(분기 카드 포함)를 보고, 빈소 없음이면 Q1 으로 간다
  | "familyIntro";

// ---------------------------------------------------------------------------
// 첫 질문 - 관심 주제 (funnel 결정)
// ---------------------------------------------------------------------------
export const MUBINSO_TOPIC_TITLE = "어떤 부분이 궁금하신가요?";

// event: click-mubinso_funnel-{event} 로 발화 (응답별 선택 비율 분석용)
export const MUBINSO_TOPIC_OPTIONS = [
  {
    label: "무빈소 장례는 어떻게 진행되는지 알고싶어요",
    funnel: "procedure",
    event: "topic-procedure",
  },
  { label: "무빈소 장례의 실제 비용이 궁금해요", funnel: "cost", event: "topic-cost" },
  {
    label: "무빈소 장례가 가능한 장례식장을 찾고싶어요",
    funnel: "parlorFind",
    event: "topic-parlor",
  },
  // "세가지 다" 도 절차 퍼널(B)을 본다. 다만 funnel 은 main 으로 남긴다.
  // 진입 태깅(topic_all)이 funnelType === "main" 으로 갈리기 때문이고,
  // 실제 병합은 MubinsoFunnelContainer 라우팅에서 처리한다.
  { label: "세가지 다 궁금해요", funnel: "main", event: "topic-all" },
] as const;

/**
 * UI 임박도 문구 → v2 리드 status enum. 문구는 퍼널/모달마다 다르므로 통합 매핑.
 * 체크리스트 계열은 "미리 준비/임박/임종" 3분리(2026-07 세일즈 요청)로 텍스트가 바뀌어
 * 신규 문구("임종이 임박하신 상태입니다.")도 같이 매핑해야 한다.
 * 알 수 없는 문구는 "임종" 포함 시 임종상태로만 보수 처리, 아니면 미전송(undefined).
 */
export const mubinsoLeadStatus = (label: string): LeadUrgencyStatus | undefined => {
  switch (label) {
    case "임종하신 상태입니다.":
    case "임종을 앞두거나 임종하신 상태입니다.":
      return "임종상태";
    case "임종이 임박하신 상태입니다.":
    case "임종이 며칠 남지 않았어요.":
      return "임박";
    case "1주에서 한 달 정도 기간이 남은 것 같아요.":
      return "1달";
    case "급하지 않지만 미리 알아보려고 해요.":
    case "미리 준비하려고 해요":
      return "여유";
    default:
      return label.includes("임종") ? "임종상태" : undefined;
  }
};

// ---------------------------------------------------------------------------
// 둘째 질문 - 지금 상황 (긴급도)
// ---------------------------------------------------------------------------
export const MUBINSO_SITUATION_TITLE = "지금 상황을 알려주세요";

// urgent(임종/임박) = 주제 무관 메인 퍼널 + 10분 이내 상담 모달
export const MUBINSO_SITUATION_OPTIONS = [
  { label: "임종하신 상태입니다.", urgent: true, event: "situation-passed" },
  { label: "임종이 며칠 남지 않았어요.", urgent: true, event: "situation-days" },
  { label: "1주에서 한 달 정도 기간이 남은 것 같아요.", urgent: false, event: "situation-month" },
  { label: "급하지 않지만 미리 알아보려고 해요.", urgent: false, event: "situation-later" },
] as const;

export const topicOptionByLabel = (label: string) =>
  MUBINSO_TOPIC_OPTIONS.find((o) => o.label === label);

export const situationOptionByLabel = (label: string) =>
  MUBINSO_SITUATION_OPTIONS.find((o) => o.label === label);

export const isUrgentSituation = (label: string): boolean =>
  situationOptionByLabel(label)?.urgent ?? false;

/**
 * 최종 퍼널 결정. 긴급(임종/임박)이면 주제 무관 메인이되, **장례식장 찾기 + B안**만 예외다.
 * (실험 시절 로직 유지 - 질문 순서만 주제→상황으로 스왑)
 *
 * 왜 장례식장 찾기를 임박도에서 빼는가 (2026-09-18 BigQuery 60일 실측)
 * - 임박/임종 + 장례식장찾기의 리드 제출률은 20.3%(13/64)로, 같은 임박도의 다른 주제
 *   5.1%(26/507)의 **4배**다(z=4.54, p<0.001). 임박도가 아니라 주제가 집단을 가른다.
 * - 1달/여유 + 장례식장찾기 25.4%(74/291)와는 차이가 없다(z=0.86, p=0.389).
 *
 * 왜 A·B 공통이 아니라 실험 안에 넣는가 (2026-09-19 변경)
 * - 그 20.3% 는 **메인 퍼널을 본** 사람들의 값이다. 찾기 퍼널로 보내면 더 오른다는 것은
 *   추론이고 반대도 가능하다. 찾기 퍼널은 info1·info2 교육 2스텝이 있어 급한 사람에게
 *   마찰이고, 메인 퍼널의 10분 이내 상담 모달도 잃는다.
 * - A·B 공통으로 적용하면 그것이 해로울 때 양쪽이 같이 떨어져 실험이 잡아내지 못한다.
 *   대조군을 현행으로 두면 최소한 A 배정자는 보호된다.
 *
 * @param allowUrgentParlorFind 임박/임종이어도 찾기 퍼널을 태울지. 실험 B안에서만 true
 */
export const resolveFunnel = (
  topicLabel: string,
  situationLabel: string,
  allowUrgentParlorFind = false,
): MubinsoFunnelType => {
  const topicFunnel = topicOptionByLabel(topicLabel)?.funnel;
  const urgent = isUrgentSituation(situationLabel);

  // 여유는 종전대로 주제를 따르고, 임박/임종은 B안일 때만 찾기 퍼널로 간다
  if (topicFunnel === "parlorFind" && (!urgent || allowUrgentParlorFind)) return "parlorFind";

  if (urgent) return "main";
  return topicFunnel ?? "main";
};

// ---------------------------------------------------------------------------
// 랜딩(무빈소 장례 안내)
// ---------------------------------------------------------------------------
export const MUBINSO_LANDING = {
  headline: "무빈소 장례를\n알아보고 계신가요?",
  sub: "아직 낯선 방식이라\n어디서부터 알아봐야 할지 막막하실 거예요.",
  cardTitle: "이런 내용을 무료로 알려드려요",
  cardItems: ["무빈소 장례란? (절차와 비용)", "장점과 단점", "결정 전 꼭 확인할 사항"],
  trust:
    "장례 문화를 바꿔가고 있는 고이장례연구소가 무빈소 장례 1,000건 이상을 진행하며 정리한 내용이에요.",
  cta: "무빈소 장례 알아보기",
} as const;

export const MUBINSO_PHONE = "1666-9784";

// 최종 가이드 화면에 노출할 가이드북 팁 2개.
// 썸네일은 CDN 규칙(getCdnImageSrc로 절대경로화): images/guidebook/tip/thumbnail/{category}/{index}.png
// 존재하지 않으면 MUBINSO_GUIDE_FALLBACK_IMG로 대체(엑박 방지).
export const MUBINSO_GUIDE_FALLBACK_IMG = "images/guidebook/guidebook_info_1.png";

export const MUBINSO_GUIDE_BOOKS = [
  {
    href: "/guidebook/tip/ongoing/3/",
    thumbnail: "images/guidebook/tip/thumbnail/ongoing/3.png",
    alt: "무빈소 장례 진행 가이드",
    eventTarget: "guide-book_1",
  },
  {
    href: "/guidebook/tip/before/funeral-cost-save/",
    thumbnail: "images/guidebook/tip/thumbnail/before/funeral-cost-save.png",
    alt: "장례 비용 절약 가이드",
    eventTarget: "guide-book_2",
  },
] as const;

// NOTE: 장례식장 찾기 퍼널의 지역 선택은 디자인의 새 8-지역 그리드가 아니라
// 기존 match/request 지역 선택 화면을 재사용하기로 함(세부 차이는 캡쳐 전달 예정). → 별도 상수 미정의.

// ---------------------------------------------------------------------------
// M5 가족장 퍼널 (가족장 소재 n_ad, ?template=family)
// 기획: Notion 2-3-4 E3 M5 랜딩페이지 퍼널 기획, 명세: wiki/plans/mubinso-family-funnel.md
// ---------------------------------------------------------------------------

/** 네이버 SA `후불제_무빈소캠페인_v2` > `M5 가족장·2일장` 그룹의 가족장 소재(B). A 는 같은 그룹의 기존 소재 */
const FAMILY_AD_ID = "nad-a001-01-000000608567080";

/**
 * "빈소 있음" 을 고른 사람을 일반 견적으로 보낼 때 붙이는 쿼리(fam_exit=binso).
 * 광고 쿼리(n_query, n_ad, gclid)를 지우면 일반 견적이 광고 유입을 몰라 Hackle 의 광고/자연 유입 실험에
 * 자연 유입으로 배정된다. 그래서 광고 쿼리는 남기고, 이 표시로 가족장 · 무빈소 판정만 끈다.
 *
 * @param famExit - router.query.fam_exit (string | string[] | undefined)
 */
export const isFamilyBinsoExit = (famExit: string | string[] | undefined): boolean =>
  famExit === "binso";

/**
 * 가족장 퍼널 진입 여부. 가족장 소재(B)로 들어온 사람(n_ad) 또는 ?template=family 링크.
 *
 * A/B 는 광고 소재 ID 로 나뉘므로 여기서 실험 배정을 하지 않는다. 두 소재는 같은 주소
 * (?template=mubinso)를 쓰고, 네이버 자동 추적이 붙이는 n_ad 로만 가른다.
 * 무빈소 퍼널의 Hackle 실험(관심 장례식장 체크, E4 개편)에도 배정하지 않는다. 섞이면 실험 모수가 오염된다.
 * 광고 검색어(n_query)에 "무빈소" 가 있어도 이 값이 우선이다.
 *
 * 무빈소 B 랜딩(mubinsoLandingBContext)과 달리 세션에 마킹하지 않는다. "빈소 있음" 을 고르면
 * fam_exit=binso 를 붙여 일반 견적을 보여 주는데, 세션 마킹이 남으면 다시 가족장 화면이 뜬다.
 * 새로고침과 fam_step 이동은 쿼리를 그대로 두므로 n_ad 가 유지된다.
 *
 * @param template - router.query.template (string | string[] | undefined)
 * @param nAd - router.query.n_ad (string | string[] | undefined)
 * @param famExit - router.query.fam_exit (string | string[] | undefined). "빈소 있음" 으로 나간 뒤면 false
 */
export const isFamilyFunnel = (
  template: string | string[] | undefined,
  nAd: string | string[] | undefined,
  famExit: string | string[] | undefined,
): boolean => !isFamilyBinsoExit(famExit) && (template === "family" || nAd === FAMILY_AD_ID);

/**
 * 가족장 단계 쿼리(fam_step) → 화면. 소개 → Q1 으로 넘어갈 때 브라우저 기록을 쌓는 데 쓴다.
 *
 * @param step - router.query.fam_step (string | string[] | undefined)
 */
export const familyStepScreen = (step: string | string[] | undefined): MubinsoScreen =>
  step === "topic" ? "topic" : "familyIntro";

/** 가족장 화면의 고이 실적. 첫 화면 · 결과 화면 · 자료 받기 모달이 모두 이 숫자를 쓴다(A 는 "무빈소 장례 1,000건 이상") */
export const FAMILY_TRACK_RECORD = "장례 5,000건";

export const FAMILY_LANDING = {
  // 고이 박스 문장을 설명으로 올렸다(E4 무빈소 첫 화면과 같은 방식, 주완 2026-10-31)
  sub: `고이가 ${FAMILY_TRACK_RECORD}을 함께하며\n가족분들이 가장 많이 물어보신 것들만 담았어요.`,
} as const;

/**
 * 소개 화면의 분기 카드. binso 는 일반 견적(/match/request/)으로, mubinso 는 무빈소 Q1 으로 간다.
 * event: click-mubinso_funnel-{event}. path 14자라 예산 19자, 둘 다 잘리지 않는다.
 */
export const FAMILY_BRANCH_OPTIONS = [
  {
    key: "binso",
    label: "빈소 있는 가족장",
    icon: "building",
    lines: [
      { text: "조문객 50명 내외를 맞아요" },
      { text: "조문객께 식사를 대접해요" },
      { text: "입관·발인·화장 모두 진행해요" },
    ],
    event: "fam-br_binso",
  },
  {
    key: "mubinso",
    label: "빈소 없는 가족장 (무빈소)",
    icon: "icon_Graphic_Urn",
    lines: [
      { text: "빈소가 없어 조문객을 받지 않아요" },
      { text: "식사 준비 부담이 없어요" },
      { text: "입관·발인·화장 모두 진행해요" },
    ],
    event: "fam-br_mubinso",
  },
] as const;

export type FamilyBranchKey = typeof FAMILY_BRANCH_OPTIONS[number]["key"];

/** 슬랙 "궁금한 점" 자리. Q1 답 앞에 붙여 영업팀이 가족장 경로 리드임을 알아보게 한다 */
export const familyTopic = (topicLabel: string) => `가족장 · ${topicLabel}`;
